import { useState, useEffect } from 'react';
import { Briefcase } from 'lucide-react';
import { getExperiences } from '../services/experiencesApi';
import type { Experience } from '../services/experiencesApi';
import { ExperienceCard } from './ExperienceCard';
import { DATA_FR } from '../data/fr';
import { LABELS } from '../data/labels';

interface Props {
  data: typeof DATA_FR;
  labels: typeof LABELS['fr'];
}

export function ExperienceSection({ data, labels }: Props) {
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      try {
        const fetched = await getExperiences();
        if (fetched && fetched.length > 0) {
          setExperiences(fetched);
        } else {
          // Fallback to static data if array is empty
          setExperiences(data.experiences as any);
        }
      } catch (error) {
        console.error('Failed to load experiences', error);
        // Fallback to static data on error
        setExperiences(data.experiences as any);
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, [data.experiences]);

  if (loading) {
     return (
        <div>
            <h2 className="section-title mb-6">
                <Briefcase className="w-5 h-5 text-[#1f75cb]" />
                {labels.experience}
            </h2>
            <div className="space-y-4">
                {[1, 2, 3].map(i => (
                    <div key={i} className="card p-6 animate-pulse bg-white">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-4 gap-2">
                            <div className="w-full sm:w-1/2">
                                <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                                <div className="h-3 bg-gray-100 rounded w-1/3"></div>
                            </div>
                            <div className="w-full sm:w-1/4 flex flex-col sm:items-end">
                                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                                <div className="h-3 bg-gray-100 rounded w-1/3"></div>
                            </div>
                        </div>
                        <div className="space-y-2 mb-4">
                            <div className="h-3 bg-gray-100 rounded w-full"></div>
                            <div className="h-3 bg-gray-100 rounded w-5/6"></div>
                            <div className="h-3 bg-gray-100 rounded w-4/6"></div>
                        </div>
                        <div className="flex gap-2">
                            <div className="h-6 w-16 bg-gray-200 rounded-full"></div>
                            <div className="h-6 w-16 bg-gray-200 rounded-full"></div>
                            <div className="h-6 w-16 bg-gray-200 rounded-full"></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
     );
  }

  // Trier les expériences par année décroissante pour toujours avoir la dernière en haut
  const sortedExperiences = [...experiences].sort((a, b) => {
      const yearA = parseInt((a.period || "").match(/\d{4}/)?.[0] || "0", 10);
      const yearB = parseInt((b.period || "").match(/\d{4}/)?.[0] || "0", 10);
      return yearB - yearA;
  });

  return (
    <div>
      <h2 className="section-title mb-6">
        <Briefcase className="w-5 h-5 text-[#1f75cb]" />
        {labels.experience}
      </h2>
      {sortedExperiences.map((job, idx) => (
        <ExperienceCard key={job.id} job={job as any} index={idx} labels={labels} />
      ))}
    </div>
  );
}
